/* Atelea mega menu behavior. Horizon remains the owner of header and drawer state. */

const DESKTOP_QUERY = '(min-width: 1100px)';
const CLOSE_DELAY_MS = 140;
const MENU_SELECTOR = '[data-atelea-mega-menu]';

/**
 * @param {string} value
 * @returns {string}
 */
function pathFor(value) {
  try {
    return new URL(value, window.location.href).pathname;
  } catch {
    return value;
  }
}

/**
 * @typedef {HTMLElement & {
 *   dataset: DOMStringMap & {
 *     ateleaMegaOpen?: string;
 *     ateleaMegaReady?: string;
 *   };
 * }} MegaMenuRoot
 */

class AteleaHeaderMegaMenu {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = /** @type {MegaMenuRoot} */ (root);
    this.panel = /** @type {HTMLElement | null} */ (
      root.querySelector('[data-atelea-mega-panel]')
    );
    this.mobileTemplate = /** @type {HTMLElement | null} */ (
      root.querySelector('[data-atelea-mobile-products]')
    );
    this.header = document.querySelector('#header-component');
    this.trigger = null;
    this.mobileTarget = null;
    this.closeTimer = null;
    this.mediaQuery = window.matchMedia(DESKTOP_QUERY);
    this.handleResize = () => {
      this.updatePosition();
      this.syncMobile();
    };
    this.handleMediaChange = () => this.syncMobile();
    this.handleDocumentKeydown = (event) => {
      if (event.key === 'Escape' && this.root.dataset.ateleaMegaOpen === 'true') {
        this.close(true);
      }
    };
  }

  init() {
    if (
      !(this.panel instanceof HTMLElement) ||
      !(this.mobileTemplate instanceof HTMLElement)
    ) return;

    this.root.dataset.ateleaMegaReady = 'true';
    this.panel.setAttribute('aria-hidden', 'true');
    this.panel.addEventListener('pointerenter', () => this.cancelClose());
    this.panel.addEventListener('pointerleave', () => this.scheduleClose());
    this.panel.addEventListener('focusin', () => this.open());
    this.panel.addEventListener('focusout', () => this.scheduleClose());
    this.bindTrigger();
    this.syncMobile();
    this.updatePosition();

    window.addEventListener('resize', this.handleResize, { passive: true });
    window.addEventListener('scroll', this.handleResize, { capture: true, passive: true });
    window.addEventListener('keydown', this.handleDocumentKeydown, { capture: true });
    this.mediaQuery.addEventListener('change', this.handleMediaChange);

    const observer = new MutationObserver(() => {
      if (!this.trigger || !document.contains(this.trigger)) this.bindTrigger();
      if (!this.mediaQuery.matches) this.moveMobileTemplate();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  bindTrigger() {
    if (!(this.panel instanceof HTMLElement)) return;

    const triggerPath = pathFor(this.root.dataset.triggerUrl || '');
    const candidates = [...document.querySelectorAll('.header-menu .menu-list__link')];
    const trigger = candidates.find(
      (candidate) =>
        candidate instanceof HTMLAnchorElement && pathFor(candidate.href) === triggerPath
    );
    if (!(trigger instanceof HTMLAnchorElement)) return;
    if (this.trigger === trigger) return;

    this.trigger = trigger;
    this.trigger.dataset.ateleaMegaTrigger = 'true';
    this.trigger.id ||= `AteleaMegaTrigger-${this.root.dataset.sectionId || 'products'}`;
    this.trigger.setAttribute('aria-haspopup', 'true');
    this.trigger.setAttribute('aria-controls', this.panel.id);
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.addEventListener('pointerenter', () => this.open());
    this.trigger.addEventListener('pointerleave', () => this.scheduleClose());
    this.trigger.addEventListener('focusin', () => this.open());
    this.trigger.addEventListener('focusout', () => this.scheduleClose());
    this.trigger.addEventListener('keydown', (event) => this.handleTriggerKeydown(event));
    this.updatePosition();
  }

  /** @param {KeyboardEvent} event */
  handleTriggerKeydown(event) {
    if (event.key === 'ArrowDown') {
      const firstLink = this.panel?.querySelector('a');
      if (!(firstLink instanceof HTMLAnchorElement)) return;
      event.preventDefault();
      this.open();
      firstLink.focus();
    }
  }

  open() {
    this.cancelClose();
    this.root.dataset.ateleaMegaOpen = 'true';
    this.panel?.setAttribute('aria-hidden', 'false');
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.updatePosition();
  }

  /** @param {boolean} restoreFocus */
  close(restoreFocus = false) {
    this.cancelClose();
    if (restoreFocus) this.trigger?.focus();
    delete this.root.dataset.ateleaMegaOpen;
    this.panel?.setAttribute('aria-hidden', 'true');
    this.trigger?.setAttribute('aria-expanded', 'false');
  }

  cancelClose() {
    if (this.closeTimer !== null) window.clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  scheduleClose() {
    this.cancelClose();
    this.closeTimer = window.setTimeout(() => {
      const active = document.activeElement;
      const focusInside =
        active instanceof Node &&
        (this.panel?.contains(active) || this.trigger === active);
      if (this.trigger?.matches(':hover') || this.panel?.matches(':hover') || focusInside) return;
      this.close();
    }, CLOSE_DELAY_MS);
  }

  updatePosition() {
    if (!this.mediaQuery.matches || !(this.header instanceof HTMLElement)) return;
    const headerBottom = this.header.getBoundingClientRect().bottom;
    this.panel?.style.setProperty('--atl-mega-menu-top', `${Math.round(headerBottom)}px`);
  }

  syncMobile() {
    if (!(this.mobileTemplate instanceof HTMLElement)) return;
    if (this.mediaQuery.matches) {
      this.restoreMobileTemplate();
    } else {
      this.moveMobileTemplate();
    }
  }

  moveMobileTemplate() {
    if (!(this.mobileTemplate instanceof HTMLElement)) return;
    const drawerList = document.querySelector('header-drawer .menu-drawer__menu');
    if (!(drawerList instanceof HTMLUListElement)) return;

    const triggerPath = pathFor(this.root.dataset.triggerUrl || '');
    const items = [...drawerList.children].filter(
      (item) => item instanceof HTMLLIElement
    );
    const target = items.find((item) => {
      const link = [...item.children].find((child) => child instanceof HTMLAnchorElement);
      return link instanceof HTMLAnchorElement && pathFor(link.href) === triggerPath;
    }) || items[0];
    if (!(target instanceof HTMLLIElement)) return;

    target.dataset.ateleaMobileProducts = 'true';
    if (this.mobileTemplate.parentElement !== target) target.append(this.mobileTemplate);
    this.mobileTemplate.removeAttribute('hidden');
    this.mobileTarget = target;
  }

  restoreMobileTemplate() {
    if (!(this.mobileTemplate instanceof HTMLElement)) return;
    if (this.mobileTarget instanceof HTMLLIElement) {
      delete this.mobileTarget.dataset.ateleaMobileProducts;
      this.mobileTarget = null;
    }
    if (this.mobileTemplate.parentElement !== this.root) this.root.append(this.mobileTemplate);
    this.mobileTemplate.setAttribute('hidden', '');
    this.close();
  }
}

/**
 * @param {Document | Element} root
 */
function start(root = document) {
  const menus =
    root instanceof Element && root.matches(MENU_SELECTOR)
      ? [root]
      : [...root.querySelectorAll(MENU_SELECTOR)];

  for (const menu of menus) {
    if (!(menu instanceof HTMLElement) || menu.dataset.ateleaMegaReady) continue;
    new AteleaHeaderMegaMenu(menu).init();
  }
}

start();
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) start(event.target);
});
